import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Truck, MapPin, Zap, Warehouse, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import USMap from '../components/USMap';
import EstimateWidget from '../components/EstimateWidget';
import SEO from '../seo/SEO';
import {
  ZONE_LEGEND,
  STATE_ZONES,
  STATE_NAME_TO_CODE,
  CODE_TO_STATE_NAME,
  POPULATION_REACH,
  ORIGIN,
  getZoneInfo,
  lookupStateFromZip,
} from '../data/zones';

function CarrierPill({ name }) {
  return (
    <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold">
      {name}
    </span>
  );
}

function ZoneLegend() {
  return (
    <aside className="bg-white rounded-2xl border border-slate-200 p-6 w-full lg:w-72 lg:flex-shrink-0">
      <h3 className="text-base font-bold text-slate-900 mb-4">Transit zones</h3>
      <ul className="space-y-3">
        {ZONE_LEGEND.map((z) => (
          <li key={z.zone} className="flex items-start gap-3">
            <span
              className="flex-shrink-0 w-3.5 h-3.5 rounded-full mt-1"
              style={{ backgroundColor: z.color }}
            />
            <div>
              <p className="text-sm font-semibold text-slate-900">Zone {z.zone}</p>
              <p className="text-xs text-slate-500">{z.label}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500 leading-relaxed">
        Estimates from USPS/UPS/FedEx zone charts for origin ZIP {ORIGIN.zip}. Actual transit may
        vary by carrier service and weather.
      </p>
    </aside>
  );
}

function CheckTransit() {
  const [mode, setMode] = useState('zip');
  const [zip, setZip] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [result, setResult] = useState(null);

  const stateOptions = useMemo(
    () =>
      Object.entries(STATE_NAME_TO_CODE)
        .map(([name, code]) => ({ name, code }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const handleCheck = () => {
    let code = null;
    if (mode === 'zip') {
      const cleaned = (zip || '').trim();
      if (!/^\d{5}$/.test(cleaned)) {
        setResult({ error: 'Please enter a valid 5-digit ZIP code.' });
        return;
      }
      code = lookupStateFromZip(cleaned);
    } else {
      code = stateCode;
    }
    if (!code || !STATE_ZONES[code]) {
      setResult({ error: 'We could not match that location to a zone. Try a state instead.' });
      return;
    }
    const zone = STATE_ZONES[code];
    const info = getZoneInfo(zone);
    setResult({ code, zone, label: info.label, color: info.color, name: CODE_TO_STATE_NAME[code] });
  };

  return (
    <section className="py-20 sm:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
          Check transit time to your customer
        </h2>
        <p className="mt-4 text-lg text-slate-600">
          Enter a ZIP or pick a state to see the typical ground transit time from our{' '}
          {ORIGIN.city} warehouse.
        </p>

        <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-2">
          <div className="flex gap-1 bg-white rounded-xl p-1 mb-3">
            <button
              type="button"
              onClick={() => setMode('zip')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'zip'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              By ZIP code
            </button>
            <button
              type="button"
              onClick={() => setMode('state')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'state'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              By state
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 p-2">
            {mode === 'zip' ? (
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="e.g. 90210"
                className="h-12 text-base rounded-xl"
                inputMode="numeric"
              />
            ) : (
              <select
                value={stateCode}
                onChange={(e) => setStateCode(e.target.value)}
                className="flex-1 h-12 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a state...</option>
                {stateOptions.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            <Button
              onClick={handleCheck}
              className="h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold"
            >
              <span className="inline-flex items-center gap-2">
                <Search className="w-4 h-4" /> Check
              </span>
            </Button>
          </div>
        </div>

        {result && (
          <div
            className={`mt-6 p-6 rounded-2xl border text-left animate-fade-up ${
              result.error
                ? 'bg-rose-50 border-rose-200'
                : 'bg-white border-indigo-200 shadow-sm'
            }`}
          >
            {result.error ? (
              <p className="text-rose-700 font-medium">{result.error}</p>
            ) : (
              <div className="flex items-center gap-4">
                <span
                  className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-white font-extrabold text-lg"
                  style={{ backgroundColor: result.color }}
                >
                  Z{result.zone}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium">
                    From {ORIGIN.city} to {result.name}
                  </p>
                  <p className="text-xl font-extrabold text-slate-900 mt-0.5">
                    Zone {result.zone} · ~{result.label}
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold">
                  <Truck className="w-4 h-4" /> Ground
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default function ZoneMap() {
  const location = useLocation();

  // Smooth-scroll to hash target on initial load / when hash changes
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="US Shipping Zones Map & Instant Fulfillment Estimate"
        description="Interactive US shipping zone map from our Weston, FL warehouse. See transit times by state and get an instant fulfillment cost estimate for your Shopify or DTC store."
        keywords="shipping zones map, US shipping zones, fulfillment cost calculator, shipping zone calculator by ZIP, 3PL transit times, shipping from Florida, ground transit map"
      />
      <Header />

      {/* Hero + map */}
      <section className="relative pt-12 sm:pt-16 pb-16 sm:pb-20 brand-soft-bg overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-indigo-200/30 blur-3xl" />
          <div className="absolute top-40 -left-32 w-[360px] h-[360px] rounded-full bg-blue-200/30 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5" /> Ground Shipping Zone Map
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              One warehouse.{' '}
              <span className="brand-gradient-text">The Eastern US in 1–3 days.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600">
              Our {ORIGIN.city} dock is the fastest gateway to the Southeast and the entire Eastern
              Seaboard — your orders ship economy ground and still arrive like express.
            </p>

            <div className="mt-7 inline-flex flex-wrap items-center justify-center gap-2">
              <CarrierPill name="UPS" />
              <span className="text-slate-300">·</span>
              <CarrierPill name="FedEx" />
              <span className="text-slate-300">·</span>
              <CarrierPill name="USPS Ground" />
            </div>
          </div>

          <div className="mt-12 flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1 bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-sm">
              <USMap />
              <p className="mt-3 text-center text-xs text-slate-500 max-w-2xl mx-auto">
                UPS Ground, FedEx Ground, and USPS Ground Advantage all use the same zone bands
                from origin ZIP {ORIGIN.zip}. Most lanes match within a day.
              </p>
            </div>
            <ZoneLegend />
          </div>
        </div>
      </section>

      {/* Population reach */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-indigo-600">
            US Population Reach
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            How far ground actually goes
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Standard ground transit time from our {ORIGIN.city} dock to US households, based on
            USPS/UPS/FedEx zone data.
          </p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {POPULATION_REACH.map((p) => (
              <div
                key={p.days}
                className="bg-white border border-slate-200 rounded-2xl py-7 px-4 hover:border-indigo-200 hover:shadow-md transition-all duration-200"
              >
                <p className="text-4xl sm:text-5xl font-extrabold brand-gradient-text">
                  {p.percent}%
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-600">in {p.days}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500 max-w-3xl mx-auto">
            Figures reflect standard ground service from origin ZIP {ORIGIN.zip}. Actual delivery
            may vary with weather, carrier capacity, and residential/commercial classification.
          </p>
        </div>
      </section>

      {/* Transit checker */}
      <CheckTransit />

      {/* What this means */}
      <section className="py-20 sm:py-24 bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              What this means for your customers
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Geography is half the fulfillment game. {ORIGIN.city} puts you within reach of the
              Southeast and Eastern Seaboard without a sprawling warehouse network.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Benefit
              icon={Zap}
              title="Most Eastern US in 1–3 days"
              body="South Florida positioning means standard ground covers the entire Southeast and Mid-Atlantic fast. No paying for air to hit reasonable delivery promises."
            />
            <Benefit
              icon={Warehouse}
              title="One warehouse, simpler ops"
              body="No splitting inventory across nodes. No transfer fees. No guessing where to allocate units. Just one place, one source of truth."
            />
            <Benefit
              icon={Calculator}
              title="The map, the rates, the math"
              body="We give you transit estimates, carrier rates, and the exact pick/pack cost upfront. You decide what makes sense for your customers."
            />
          </div>
        </div>
      </section>

      {/* CTA / Instant Estimate Widget */}
      <EstimateWidget />

      <Footer />
    </div>
  );
}

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-indigo-200 hover:shadow-md transition-all duration-200">
      <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-indigo-600" strokeWidth={2} />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
